import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Input from '../shared/TailwindComponents/Input';
import CurrencyPicker from '../shared/TailwindComponents/CurrencyPicker';
import CurrencyInput from '../shared/TailwindComponents/CurrencyInput';
import {
  PRICING_MODELS,
  PRICING_MODEL_LABEL_KEYS,
  requiresFixedFee,
  requiresContingency,
  getPricingErrors,
} from '../shared/economics/pricingModel';
import { MatterPricingValue, emptyMatterPricing } from './economics/economicsConstants';
import type { PricingModel } from '../../types/economics';

/**
 * The Billing section of the matter wizard.
 *
 * The shared pricing form is one block - its own heading and intro under the
 * wizard's Billing heading, then five fields each with a help line - which on
 * Details read as a wall. Here the same fields sit in two titled groups with
 * room between them: how the matter is billed (model, currency) and the
 * amounts (budget, plus the fee or percentage the model needs). Same value
 * shape and the same validation as everywhere else.
 */
interface WizardBillingFieldsProps {
  value?: MatterPricingValue;
  onChange: (v: MatterPricingValue) => void;
  defaultCurrency?: string;
}

const SubSection: React.FC<{ title: string; description: string; children: React.ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <div>
    <p className="text-sm font-medium text-fg-strong">{title}</p>
    <p className="mt-0.5 mb-3 text-xs text-fg-muted">{description}</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </div>
);

const WizardBillingFields: React.FC<WizardBillingFieldsProps> = ({ value, onChange, defaultCurrency }) => {
  const { t } = useTranslation();
  // Errors show once the user has touched the section, as before.
  const [touched, setTouched] = useState(false);
  const v = value || emptyMatterPricing(defaultCurrency || 'usd');
  const errors = touched ? getPricingErrors(v) : {};
  const set = (patch: Partial<MatterPricingValue>) => {
    setTouched(true);
    onChange({ ...v, ...patch });
  };

  const modelOptions = PRICING_MODELS.map((m) => ({ value: m, label: t(PRICING_MODEL_LABEL_KEYS[m]) }));

  return (
    <div className="space-y-6">
      <SubSection
        title={t('Pricing')}
        description={t('Set how this {{entity}} is billed and in which currency.', { entity: t('matter') })}
      >
        <Dropdown
          id="matter-pricing-model"
          label={t('Pricing model')}
          options={modelOptions}
          placeholder={t('Select a pricing model')}
          value={v.pricing_model}
          onChange={(selected) =>
            set({ pricing_model: (Array.isArray(selected) ? selected[0] : selected) as PricingModel })
          }
          className="w-full"
        />
        <CurrencyPicker
          id="matter-currency"
          label={t('Currency')}
          value={v.currency}
          onChange={(currency) => set({ currency })}
          className="w-full"
        />
      </SubSection>

      <SubSection
        title={t('Fees and budget')}
        description={t('The budget is optional. Fee fields follow the pricing model.')}
      >
        <CurrencyInput
          id="matter-budget-amount"
          label={t('Budget')}
          value={v.budget_amount}
          currency={v.currency}
          onChange={(budget_amount) => set({ budget_amount })}
          error={errors.budget_amount ? t(errors.budget_amount) : undefined}
          className="w-full"
        />
        {requiresFixedFee(v.pricing_model) && (
          <CurrencyInput
            id="matter-fixed-fee-amount"
            label={t('Fixed fee amount')}
            value={v.fixed_fee_amount}
            currency={v.currency}
            onChange={(fixed_fee_amount) => set({ fixed_fee_amount })}
            error={errors.fixed_fee_amount ? t(errors.fixed_fee_amount) : undefined}
            required
            className="w-full"
          />
        )}
        {requiresContingency(v.pricing_model) && (
          <Input
            type="number"
            id="matter-contingency-pct"
            label={t('Contingency %')}
            placeholder="0"
            min={0}
            value={v.contingency_pct}
            onChange={(e) => set({ contingency_pct: e.target.value })}
            error={errors.contingency_pct ? t(errors.contingency_pct) : undefined}
            required
            suffix={<span className="text-fg-muted">%</span>}
            className="w-full"
          />
        )}
      </SubSection>
    </div>
  );
};

export default WizardBillingFields;
